import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Eye,
  FileText,
  Heart,
  HelpCircle,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
  X
} from 'lucide-react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Legend
} from 'recharts';

type TabId = 'esprit' | 'cadre' | 'maturite' | 'raci' | 'roadmap' | 'rituels';

interface GouvernanceLoi27ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="bg-gradient-to-r from-rose-600/20 via-pink-600/20 to-rose-600/20 rounded-xl p-6 border border-rose-500/30">
      <h3 className="text-2xl font-bold text-center text-white mb-2">{title}</h3>
      {subtitle ? <p className="text-gray-200 text-center text-sm">{subtitle}</p> : null}
    </div>
  );
}

function InfoCard({
  title,
  icon,
  bullets,
  theme = 'rose'
}: {
  title: string;
  icon?: React.ReactNode;
  bullets: string[];
  theme?: 'rose' | 'pink';
}) {
  const border = theme === 'pink' ? 'border-pink-500/20' : 'border-rose-500/20';
  const iconColor = theme === 'pink' ? 'text-pink-300' : 'text-rose-300';
  return (
    <div className={`bg-white/5 rounded-xl p-6 border ${border}`}>
      <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
        {icon ? <span className={iconColor}>{icon}</span> : null}
        <span>{title}</span>
      </h4>
      <ul className="space-y-2 text-sm text-gray-200 list-disc ml-5">
        {bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// PILIERS LOI 27 (cadre holistique — maturité 0-5)
// ============================================================================
type NiveauMaturite = { niveau: number; label: string; description: string };
type PilierLoi27 = {
  id: number;
  nom: string;
  article?: string;
  actuel: number;
  cible: number;
  intention: string;
  pourquoiCritique: string;
  concretementBZ: string;
  erreursFrequentes: string[];
  niveaux: NiveauMaturite[];
  preuvesVisibles: string[];
};

const piliersLoi27: PilierLoi27[] = [
  {
    id: 1,
    nom: 'Programme de prévention',
    article: 'LSST art. 59, 62',
    actuel: 1.5,
    cible: 3,
    intention: 'Documenter les risques (physiques et psychosociaux), les moyens de prévention et les mécanismes de suivi pour que l\'employeur et les travailleurs sachent quoi faire et puissent prouver la diligence.',
    pourquoiCritique: 'Sans programme écrit et à jour, aucune conformité possible. La CNESST peut exiger le document ; les visites sans préavis et les ordonnances publiques (Loi 27) rendent la preuve indispensable.',
    concretementBZ: 'Document unique, versionné, accessible (intranet ou dossier partagé). Contenu : identification des risques par milieu, mesures de prévention, désignation du RSS, intégration des RPS, mécanismes de révision. Révision au moins annuelle ou lors de changements significatifs.',
    erreursFrequentes: [
      'Programme générique copié sans adaptation au contexte BZ.',
      'Programme non communiqué aux travailleurs (obligation de le porter à leur connaissance).',
      'RPS absents ou traités en une phrase.',
      'Pas de date de révision ni de responsable identifié.',
    ],
    niveaux: [
      { niveau: 0, label: 'Inexistant', description: 'Aucun document ou document obsolète.' },
      { niveau: 1, label: 'Brouillon', description: 'Document partiel, non diffusé.' },
      { niveau: 2, label: 'Documenté', description: 'Programme écrit, connu des principaux acteurs.' },
      { niveau: 3, label: 'Conforme', description: 'Complet (risques physiques + RPS), à jour, communiqué, révision planifiée.' },
      { niveau: 4, label: 'Intégré', description: 'Référence dans les formations, intégré aux rituels (comité SST, 1:1).' },
      { niveau: 5, label: 'Exemplaire', description: 'Piloté par indicateurs, amélioration continue, preuves systématiques.' },
    ],
    preuvesVisibles: ['Programme versionné', 'Attestation de communication aux travailleurs', 'Dates de révision', 'Annexe RPS'],
  },
  {
    id: 2,
    nom: 'Comité SST paritaire',
    article: 'LSST art. 68 et suiv.',
    actuel: 2,
    cible: 3,
    intention: 'Réunir employeur et travailleurs pour examiner les conditions de travail, faire des recommandations et suivre les actions. La Loi 27 renforce le rôle du comité et la tenue de réunions régulières.',
    pourquoiCritique: 'Obligatoire pour 20+ travailleurs. Absence ou comité fantôme = risque d\'ordonnance et d’amende. Le comité est le lieu où les RPS et le programme sont discutés et suivis.',
    concretementBZ: 'Composition paritaire (même nombre de représentants employeur et travailleurs). Pour BZ (55 employés, catégorie 51-100), composition légale minimale : 3 représentants employeur (ex. Dany, Mélissa, gestionnaire) + 3 représentants travailleurs (incluant le RSS) = 6 membres minimum. Réunions à fréquence conforme (règlement), ordre du jour structuré (accidents/incidents, recommandations, RPS, suivi actions). PV rédigés, conservés et transmis. Recommandations tracées et statut (accepté/refusé/en cours).',
    erreursFrequentes: [
      'Réunions annulées ou reportées sans cesse.',
      'PV inexistants ou vagues (pas de recommandations ni de suivi).',
      'RPS jamais à l’ordre du jour.',
      'Aucun suivi des recommandations (on oublie après la réunion).',
    ],
    niveaux: [
      { niveau: 0, label: 'Aucun', description: 'Pas de comité ou comité non convoqué.' },
      { niveau: 1, label: 'Formel', description: 'Comité formé mais réunions irrégulières ou sans contenu.' },
      { niveau: 2, label: 'Opérationnel', description: 'Réunions régulières, PV basiques.' },
      { niveau: 3, label: 'Conforme', description: 'ODJ structuré, recommandations et suivi, RPS inclus.' },
      { niveau: 4, label: 'Efficace', description: 'Actions SMART, délais, propriétaires, revue trimestrielle.' },
      { niveau: 5, label: 'Exemplaire', description: 'Piloté par indicateurs (taux de clôture, délais), culture prévention.' },
    ],
    preuvesVisibles: ['Convocation et PV', 'Liste des recommandations et statut', 'Présence RPS à l’ODJ', 'Registre des actions'],
  },
  {
    id: 3,
    nom: 'Représentant SST (RSS)',
    article: 'LSST art. 68.1, 69',
    actuel: 1,
    cible: 3,
    intention: 'Une personne désignée, formée et connue de tous pour représenter les travailleurs en SST, participer à l’identification des risques et collaborer avec l’employeur et le comité. Note importante : Le RSS n\'est pas le président du comité SST — ce sont deux rôles différents. Le RSS représente les travailleurs et siège au comité en tant que membre, tandis que le président anime les réunions (peut être un représentant employeur ou travailleur selon l\'entente).',
    pourquoiCritique: 'Obligatoire (1 à 19 : agent de liaison ; 20+ : RSS). Absence = non-conformité. Le RSS est un relais terrain et une preuve que l’employeur prend la SST au sérieux.',
    concretementBZ: 'Désignation formalisée (procès-verbal ou écrit), formation CNESST ou reconnue documentée, nom et coordonnées communiqués à tous (affichage, intranet, courriel). Le RSS siège au comité SST (en tant que membre, pas président) et est consulté sur le programme de prévention et les RPS.',
    erreursFrequentes: [
      'RSS non désigné ou désignation floue.',
      'Formation non documentée (pas de preuve en cas d’inspection).',
      'Personne ne sait qui est le RSS.',
      'RSS jamais consulté sur les changements ou les RPS.',
    ],
    niveaux: [
      { niveau: 0, label: 'Aucun', description: 'Pas de RSS désigné.' },
      { niveau: 1, label: 'Désigné', description: 'Nom connu mais formation ou rôle flou.' },
      { niveau: 2, label: 'Formé', description: 'Formation documentée, communiqué interne.' },
      { niveau: 3, label: 'Conforme', description: 'Désignation + formation + connu de tous + impliqué (comité, programme).' },
      { niveau: 4, label: 'Actif', description: 'Présent aux réunions, consulté sur les risques et RPS.' },
      { niveau: 5, label: 'Référent', description: 'Reconnu comme relais terrain, indicateurs de satisfaction.' },
    ],
    preuvesVisibles: ['Procès-verbal de désignation', 'Attestation de formation', 'Communication (courriel, affichage)', 'Présence aux PV comité'],
  },
  {
    id: 4,
    nom: 'Risques psychosociaux (RPS)',
    article: 'Loi 27, échéance 6 oct. 2025',
    actuel: 1,
    cible: 3,
    intention: 'Identifier, évaluer et contrôler les risques psychosociaux (harcèlement, violence, charge de travail, reconnaissance, etc.) comme tout autre risque SST, avec politique, formation et suivi.',
    pourquoiCritique: 'La Loi 27 impose explicitement les RPS. Les ignorer expose à des ordonnances et à des réclamations. Une politique et des mécanismes clairs protègent l’employeur et les travailleurs.',
    concretementBZ: 'Politique RPS (harcèlement, violence, charge, reconnaissance) ; intégration dans le programme de prévention ; formation gestionnaires et employés ; indicateurs (sondages, 1:1, plaintes) ; RPS à l’ODJ du comité SST ; mesures correctives documentées.',
    erreursFrequentes: [
      'RPS absents du programme ou traités en une ligne.',
      'Politique générique sans mécanisme de plainte ou de suivi.',
      'Aucune formation sur les RPS.',
      'Comité SST ne traite jamais des RPS.',
    ],
    niveaux: [
      { niveau: 0, label: 'Inexistant', description: 'Aucune mention des RPS.' },
      { niveau: 1, label: 'En préparation', description: 'Politique brouillon ou formation ponctuelle.' },
      { niveau: 2, label: 'Documenté', description: 'Politique en place, RPS dans le programme, début de formation.' },
      { niveau: 3, label: 'Conforme', description: 'Politique + programme + formation + RPS au comité + suivi.' },
      { niveau: 4, label: 'Intégré', description: 'Indicateurs suivis, revue trimestrielle, gestionnaires formés.' },
      { niveau: 5, label: 'Exemplaire', description: 'Culture prévention RPS, feedback terrain, amélioration continue.' },
    ],
    preuvesVisibles: ['Politique RPS', 'Programme (section RPS)', 'Preuves de formation', 'PV comité (points RPS)', 'Registre plaintes / suivi'],
  },
];

// ============================================================================
// AVATARS (Mélissa = MP, Dany = DB, Dany = YM, Guillaume = GC)
// ============================================================================
const AVATAR_YM = '/DB.png'; // Dany Bourassa
const AVATAR_MP = '/MP.png'; // Mélissa Pelletier
const AVATAR_DB = '/DB.png'; // Dany Bourassa
const AVATAR_GC = '/GC.png'; // Guillaume Cloutier

// ============================================================================
// LIENS OFFICIELS (réutilisable)
// ============================================================================
const LIEN_CNESST_MODERNISATION = 'https://www.cnesst.gouv.qc.ca/salle-de-presse/loi-modernisant-regime-sst/Pages/loi-modernisant-regime-sst.aspx';
const LIEN_LOI_CANLII = 'https://canlii.ca/t/dkwp#sec1';
const LIEN_CNESST_RPS = 'https://www.cnesst.gouv.qc.ca/sante-securite-travail/prevention/Pages/risques-psychosociaux.aspx';

function LiensOfficielsBlock() {
  return (
    <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
      <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <ExternalLink className="w-5 h-5 text-rose-300" />
        Références officielles (Loi 27 / LMRSST)
      </h4>
      <ul className="space-y-3 text-sm">
        <li>
          <a
            href={LIEN_CNESST_MODERNISATION}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-rose-200 hover:text-rose-100 underline"
          >
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            CNESST — Loi modernisant le régime de santé et de sécurité du travail
          </a>
        </li>
        <li>
          <a
            href={LIEN_LOI_CANLII}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-rose-200 hover:text-rose-100 underline"
          >
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            Loi sur la santé et la sécurité du travail (L.R.Q., chapitre S-2.1) — CanLII
          </a>
        </li>
        <li>
          <a
            href={LIEN_CNESST_RPS}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-rose-200 hover:text-rose-100 underline"
          >
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            CNESST — Risques psychosociaux (RPS)
          </a>
        </li>
      </ul>
    </div>
  );
}

export default function GouvernanceLoi27Modal({ isOpen, onClose }: GouvernanceLoi27ModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');
  const [expandedPilierLoi27, setExpandedPilierLoi27] = useState<number | null>(null);
  const [isGuideReunionOpen, setIsGuideReunionOpen] = useState(false);

  const tabs = useMemo(
    () => [
      { id: 'esprit' as const, label: 'Esprit & Objectifs macro', icon: Eye },
      { id: 'cadre' as const, label: 'Cadre holistique (Loi 27)', icon: BookOpen },
      { id: 'maturite' as const, label: 'Maturité', icon: Target },
      { id: 'raci' as const, label: 'Rôles & responsabilités', icon: Users },
      { id: 'roadmap' as const, label: 'Roadmap', icon: Calendar },
      { id: 'rituels' as const, label: 'Gouvernance & rituels', icon: ShieldCheck },
    ],
    []
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-rose-900/50 to-slate-900 rounded-2xl w-[80vw] h-[80vh] border border-rose-500/30 shadow-2xl shadow-rose-500/10 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-rose-500/30 bg-gradient-to-r from-rose-900/40 to-slate-900/40 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-pink-300">
                        Gouvernance — Loi 27 (LMRSST)
                      </h2>
                      <p className="text-gray-300 text-xs">
                        Comité RH • Santé et sécurité du travail • Conformité et risques psychosociaux
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-500/30 border border-rose-400/50 text-xs text-rose-200">
                          Conforme • On a commencé
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <img src={AVATAR_YM} alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Dany Bourassa — Owner" />
                      <img src={AVATAR_MP} alt="Mélissa Pelletier" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Mélissa Pelletier — Responsable" />
                      <img src={AVATAR_DB} alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Dany Bourassa — Consulté" />
                      <img src={AVATAR_GC} alt="Guillaume Cloutier" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Guillaume Cloutier — Membre" />
                    </div>
                  </div>
                  <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2">
                    <X className="w-5 h-5 text-gray-300" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-rose-500/20 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto flex-shrink-0">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-rose-600 to-pink-600 text-white shadow-lg'
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && (
                    <motion.div
                      key="esprit"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="space-y-6"
                    >
                      <SectionTitle
                        title="Esprit & objectifs macro"
                        subtitle="Vision, principes et garde-fous du cadre Loi 27 chez BZ."
                      />

                      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-rose-300" />
                          Vision — Ce que ce cadre incarne chez BZ
                        </h4>
                        <p className="text-gray-200 text-sm leading-relaxed mb-3">
                          BZ a 55 employés : nous sommes dans le régime « 20 travailleurs et plus » de la Loi 27, et plus précisément dans la catégorie 51-100 travailleurs (minimum 3 représentants de chaque côté au comité SST). Ce n’est pas une option de « nice to have » — c’est une obligation légale avec des échéances (1er octobre 2025 pour la mise en œuvre générale, 6 octobre 2025 pour les RPS), des visites CNESST sans préavis et des ordonnances publiques en cas de manquement.
                        </p>
                        <p className="text-gray-200 text-sm leading-relaxed">
                          Ce cadre existe pour <strong className="text-white">donner le ton</strong> : une organisation qui prend au sérieux la santé et la sécurité du travail (physique et psychologique), qui documente ses actions et qui peut <strong className="text-white">prouver sa diligence</strong> en cas d’inspection ou de réclamation. Il protège à la fois l’employeur et les travailleurs en rendant les droits et obligations explicites, et en faisant du comité SST et du RSS des leviers réels de prévention, pas des figurants.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <InfoCard
                          title="Principes directeurs"
                          icon={<Target className="w-5 h-5" />}
                          bullets={[
                            "Tout doit être écrit et prouvable : si la loi demande quelque chose, on doit avoir un document qui le prouve (ex. : le programme de prévention, les procès-verbaux des réunions, la désignation du RSS, la politique RPS, les preuves de formation).",
                            "Le comité SST et le RSS doivent être réels et actifs : on tient vraiment les réunions, on suit les recommandations, le RSS est formé et tout le monde sait qui c'est.",
                            "Les risques psychosociaux sont aussi importants que les risques physiques : on les identifie, on a une politique, on forme les gens et on en parle au comité SST.",
                            "On améliore constamment : on révise le programme et les mesures régulièrement, ce n'est pas quelque chose qu'on fait une fois et qu'on oublie.",
                          ]}
                        />
                        <InfoCard
                          title="Ce qu'il protège"
                          icon={<ShieldCheck className="w-5 h-5" />}
                          bullets={[
                            "L'employeur : démonstration de la diligence raisonnable en cas de plainte ou d'inspection CNESST.",
                            "Les travailleurs : conditions de travail plus saines, mécanismes de plainte et de suivi clairs.",
                            "La crédibilité : BZ peut montrer qu'elle a mis en place les mécanismes requis et qu'elle les fait vivre.",
                          ]}
                        />
                      </div>

                      <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-400" />
                          Ce que ce cadre ne doit jamais devenir
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-200">
                          <li className="flex items-start gap-2">
                            <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <span><strong className="text-white">Une coquille vide</strong> — Réunions fantômes, PV inexistants ou vagues, registres introuvables. En inspection, ça ne tient pas.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <span><strong className="text-white">Un oubli des RPS</strong> — La Loi 27 impose explicitement la prise en compte des risques psychosociaux (échéance 6 octobre 2025). Les ignorer = non-conformité et risque d’ordonnance.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <span><strong className="text-white">Un comité « PowerPoint »</strong> — Réunions sans décisions, recommandations sans suivi, actions correctives jamais clôturées.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <span><strong className="text-white">Un programme non communiqué</strong> — La loi exige que le programme de prévention soit porté à la connaissance des travailleurs. Document dans un tiroir = non-conformité.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <span><strong className="text-white">Une bureaucratie sans impact</strong> — Le but est la prévention réelle, pas remplir des cases. Les mesures doivent être appliquées et revues.</span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'cadre' && (
                    <motion.div
                      key="cadre"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="space-y-6"
                    >
                      <SectionTitle
                        title="Cadre holistique — Loi 27"
                        subtitle="4 piliers de maturité (0–5) : programme de prévention, comité SST paritaire, RSS, RPS. Chaque pilier se prouve."
                      />

                      <div className="bg-white/5 rounded-xl p-4 border border-rose-500/20">
                        <h4 className="text-sm font-bold text-rose-200 mb-2">Obligations BZ (55 employés, régime 20+)</h4>
                        <p className="text-gray-300 text-sm">
                          Programme de prévention (LSST art. 59, 62) · Comité SST paritaire (art. 68 et suiv.) · Représentant SST (art. 68.1, 69) · Identification et contrôle des risques, dont RPS (Loi 27, 6 oct. 2025).
                        </p>
                      </div>

                      {/* Piliers détaillés (expandables) */}
                      <div className="space-y-3">
                        {piliersLoi27.map((p) => (
                          <div key={p.id} className="bg-white/5 rounded-xl border border-rose-500/20 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setExpandedPilierLoi27(expandedPilierLoi27 === p.id ? null : p.id)}
                              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                                  {p.id}
                                </div>
                                <div>
                                  <div className="flex items-center flex-wrap gap-2">
                                    <h4 className="text-lg font-bold text-white">{p.nom}</h4>
                                    {p.article && (
                                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-rose-500/20 border border-rose-500/40 text-rose-200">
                                        {p.article}
                                      </span>
                                    )}
                                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-white/10 text-gray-200">
                                      AS-IS {p.actuel} → Cible {p.cible}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-gray-300 transition-transform ${expandedPilierLoi27 === p.id ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedPilierLoi27 === p.id && (
                              <div className="p-5 border-t border-rose-500/20 space-y-4">
                                <div>
                                  <h5 className="text-sm font-bold text-rose-300 mb-1">Intention</h5>
                                  <p className="text-gray-200 text-sm whitespace-pre-line">{p.intention}</p>
                                </div>
                                <div>
                                  <h5 className="text-sm font-bold text-amber-300 mb-1">Pourquoi c'est critique</h5>
                                  <p className="text-gray-200 text-sm">{p.pourquoiCritique}</p>
                                </div>
                                <div>
                                  <h5 className="text-sm font-bold text-rose-300 mb-1">Concrètement chez BZ</h5>
                                  <p className="text-gray-200 text-sm">{p.concretementBZ}</p>
                                </div>
                                <div>
                                  <h5 className="text-sm font-bold text-red-300 mb-1">Erreurs fréquentes</h5>
                                  <ul className="space-y-1">
                                    {p.erreursFrequentes.map((e, i) => (
                                      <li key={i} className="flex items-start gap-2 text-gray-200 text-sm">
                                        <X className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
                                        <span>{e}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <h5 className="text-sm font-bold text-pink-200 mb-1">Niveaux 0 → 5</h5>
                                  <ul className="space-y-1 text-gray-200 text-xs">
                                    {p.niveaux.map((n) => (
                                      <li key={n.niveau}>
                                        <span className="text-rose-300 font-semibold">{n.niveau}</span> = {n.label} — {n.description}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <h5 className="text-sm font-bold text-emerald-300 mb-1">Preuves visibles</h5>
                                  <p className="text-gray-200 text-sm">{p.preuvesVisibles.join(' · ')}</p>
                                </div>
                                {p.id === 3 && (
                                  <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                                    <h5 className="text-sm font-bold text-blue-300 mb-2 flex items-center gap-2">
                                      <AlertCircle className="w-4 h-4" />
                                      RSS vs Président du comité — Quelle différence ?
                                    </h5>
                                    <ul className="space-y-2 text-xs text-gray-200">
                                      <li>· <strong className="text-white">Oui, le RSS fait partie du comité SST</strong> : Il siège au comité en tant que membre (représentant des travailleurs), participe aux discussions et recommandations.</li>
                                      <li>· <strong className="text-white">RSS (Représentant SST)</strong> : Représente les travailleurs, participe aux réunions du comité, est consulté sur le programme de prévention et les RPS.</li>
                                      <li>· <strong className="text-white">Président du comité SST</strong> : Anime les réunions, valide les PV, peut être un représentant employeur ou travailleur selon l'entente.</li>
                                      <li>· <strong className="text-white">Ce sont deux rôles distincts</strong> : Le RSS est membre du comité, mais n'est pas nécessairement le président. Le président anime les réunions, le RSS participe en tant que membre.</li>
                                    </ul>
                                  </div>
                                )}
                                {p.id === 2 && (
                                  <>
                                    <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                                      <h5 className="text-sm font-bold text-emerald-300 mb-2 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Composition du comité SST paritaire — Combien de personnes ?
                                      </h5>
                                      <div className="space-y-2 text-xs text-gray-200">
                                        <p><strong className="text-white">Principe paritaire :</strong> Même nombre de représentants employeur et travailleurs.</p>
                                        <div className="bg-amber-500/20 rounded p-3 border border-amber-500/40 mb-2">
                                          <p className="text-amber-200 font-semibold mb-1">⚠️ Exigences légales minimales (LSST) :</p>
                                          <ul className="list-disc ml-4 space-y-1 text-xs">
                                            <li><strong className="text-white">20 à 50 travailleurs</strong> : Minimum <strong className="text-amber-200">2 représentants</strong> de chaque côté = <strong className="text-amber-200">4 membres minimum</strong></li>
                                            <li><strong className="text-white">51 à 100 travailleurs</strong> : Minimum <strong className="text-amber-200">3 représentants</strong> de chaque côté = <strong className="text-amber-200">6 membres minimum</strong></li>
                                          </ul>
                                        </div>
                                        <p><strong className="text-white">Pour BZ (55 employés — catégorie 51-100), composition légale minimale :</strong></p>
                                        <ul className="list-disc ml-5 space-y-1">
                                          <li><strong className="text-white">3 représentants employeur</strong> : Ex. Dany Bourassa (CA/CE), Mélissa Pelletier (RH), + 1 gestionnaire ou représentant employeur</li>
                                          <li><strong className="text-white">3 représentants travailleurs</strong> : Incluant le RSS (Représentant SST) + 2 autres travailleurs désignés</li>
                                          <li><strong className="text-emerald-300">Total minimum : 6 membres</strong> (3 de chaque côté)</li>
                                        </ul>
                                        <p className="mt-2"><strong className="text-white">Le RSS fait partie des représentants travailleurs.</strong></p>
                                        <p className="mt-2 text-emerald-200"><strong>Note :</strong> Le nombre peut être modifié par entente entre l'employeur et les travailleurs, mais ne peut pas être inférieur au minimum légal.</p>
                                      </div>
                                    </div>
                                    <div className="pt-2">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setIsGuideReunionOpen(true);
                                        }}
                                        className="inline-flex items-center gap-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 px-4 py-2 text-sm font-semibold text-white border border-rose-400/50 transition-colors"
                                      >
                                        <HelpCircle className="w-4 h-4" />
                                        Guide réunion — Exemple ordre du jour
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <ShieldCheck className="w-5 h-5 text-rose-300" />
                          Preuves attendues par la CNESST (en cas d'inspection)
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-200">
                          <li>· Programme de prévention à jour et preuve qu'il a été porté à la connaissance des travailleurs</li>
                          <li>· Procès-verbaux du comité SST (convocations, ODJ, recommandations, suivi)</li>
                          <li>· Désignation et formation du RSS documentées</li>
                          <li>· Politique et mesures RPS (identification, formation, suivi)</li>
                          <li>· Registre des accidents/incidents et des actions correctives</li>
                        </ul>
                      </div>

                      <div className="bg-gradient-to-r from-rose-500/15 to-pink-500/15 rounded-xl p-6 border-2 border-rose-500/40">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <Heart className="w-5 h-5 text-pink-300" />
                          Risques psychosociaux (RPS) — bases solides
                        </h4>
                        <p className="text-gray-200 text-sm mb-4">
                          La Loi 27 renforce l'obligation de prévenir les RPS (harcèlement, violence, charge de travail, reconnaissance, etc.). BZ s'appuie sur des bases solides :
                        </p>
                        <ul className="space-y-2 text-gray-200 text-sm">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                            <span>Identification des sources de RPS dans le programme de prévention et dans les activités du comité SST.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                            <span>Politiques et mécanismes (harcèlement, violence, charge de travail) alignés avec la loi et les guides CNESST.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                            <span>Formation et sensibilisation des gestionnaires et des travailleurs.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                            <span>Suivi et révision périodique : le comité SST et la RH assurent que les RPS sont à l'ordre du jour et que les actions sont suivies.</span>
                          </li>
                        </ul>
                      </div>

                      <LiensOfficielsBlock />
                    </motion.div>
                  )}

                  {activeTab === 'maturite' && (
                    <motion.div
                      key="maturite"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="space-y-6"
                    >
                      <SectionTitle
                        title="Maturité — Où on en est"
                        subtitle="Position actuelle par pilier (AS-IS), cible et focus immédiat pour atteindre la conformité aux échéances 2025."
                      />

                      <div className="bg-white/5 rounded-xl p-4 border border-rose-500/20">
                        <h4 className="text-sm font-bold text-rose-200 mb-2">Échéances Loi 27</h4>
                        <p className="text-gray-300 text-sm">
                          <strong className="text-white">1er octobre 2025</strong> — Mise en œuvre générale (programme, comité, RSS). &nbsp;
                          <strong className="text-white">6 octobre 2025</strong> — Prise en compte des risques psychosociaux (RPS).
                        </p>
                      </div>

                      {/* Spider — Maturité 4 piliers */}
                      {(() => {
                        const chartData = piliersLoi27.map((p) => ({
                          category: p.nom.length > 18 ? p.nom.substring(0, 18) + '…' : p.nom,
                          Actuel: (p.actuel / 5) * 100,
                          Cible: (p.cible / 5) * 100,
                        }));
                        const actuelMoy = (piliersLoi27.reduce((s, p) => s + p.actuel, 0) / piliersLoi27.length).toFixed(1);
                        const cibleMoy = (piliersLoi27.reduce((s, p) => s + p.cible, 0) / piliersLoi27.length).toFixed(1);
                        return (
                          <>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4">
                                <div className="text-sm text-amber-300 font-medium">Score actuel (AS-IS)</div>
                                <div className="text-2xl font-bold text-amber-200">{actuelMoy}/5</div>
                                <div className="text-xs text-amber-400">Moyenne des 4 piliers</div>
                              </div>
                              <div className="bg-rose-500/20 border border-rose-500/30 rounded-lg p-4">
                                <div className="text-sm text-rose-300 font-medium">Score cible (TARGET)</div>
                                <div className="text-2xl font-bold text-rose-200">{cibleMoy}/5</div>
                                <div className="text-xs text-rose-400">Objectif conformité 2025</div>
                              </div>
                            </div>
                            <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                              <h4 className="text-xl font-bold text-white mb-4">Graphique radar — Maturité Loi 27 (4 piliers)</h4>
                              <div className="h-80 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                  <RadarChart data={chartData} margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                                    <PolarGrid stroke="#6b7280" />
                                    <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#e5e7eb' }} />
                                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={6} />
                                    <Radar name="Actuel (AS-IS)" dataKey="Actuel" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.25} strokeWidth={2} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }} />
                                    <Radar name="Cible (TARGET)" dataKey="Cible" stroke="#f43f5e" fill="#f43f5e" fillOpacity={0.15} strokeWidth={2} dot={{ fill: '#f43f5e', strokeWidth: 2, r: 4 }} />
                                    <Legend wrapperStyle={{ fontSize: '12px', color: '#e5e7eb' }} iconType="circle" />
                                  </RadarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </>
                        );
                      })()}

                      {/* Position BZ par pilier */}
                      <div>
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <Target className="w-5 h-5 text-rose-300" />
                          Position BZ par pilier (AS-IS → Cible)
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {piliersLoi27.map((p) => (
                            <div key={p.id} className="bg-white/5 rounded-xl p-4 border border-rose-500/20">
                              <p className="font-semibold text-white text-sm mb-2">{p.nom}</p>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-2xl font-bold text-rose-300">{p.actuel}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-xl font-bold text-pink-300">{p.cible}</span>
                              </div>
                              <p className="text-xs text-gray-400">
                                {p.niveaux.find((n) => n.niveau === Math.floor(p.actuel))?.label ?? '—'} → {p.niveaux.find((n) => n.niveau === p.cible)?.label ?? '—'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Focus immédiat */}
                      <div className="bg-rose-500/10 rounded-xl p-6 border border-rose-500/30">
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-amber-300" />
                          Focus immédiat (pour viser conformité 2025)
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-200">
                          <li>· <strong className="text-white">Programme</strong> : finaliser le document (risques physiques + section RPS), le faire valider, le communiquer aux travailleurs et planifier la révision annuelle.</li>
                          <li>· <strong className="text-white">Comité SST</strong> : confirmer composition paritaire, fréquence des réunions, ODJ type (accidents, recommandations, RPS, suivi actions), et tenir les PV à jour.</li>
                          <li>· <strong className="text-white">RSS</strong> : officialiser la désignation (PV ou écrit), documenter la formation, communiquer nom et coordonnées à tous.</li>
                          <li>· <strong className="text-white">RPS</strong> : adopter ou mettre à jour la politique RPS, l’intégrer au programme, prévoir formation gestionnaires/employés et inscrire les RPS à l’ODJ du comité.</li>
                        </ul>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4 border border-rose-500/20">
                        <h4 className="text-sm font-bold text-rose-200 mb-2">Échelle de maturité (0–5)</h4>
                        <ul className="space-y-1 text-gray-200 text-xs">
                          <li><span className="font-semibold text-rose-300">0</span> = Inexistant / Aucun</li>
                          <li><span className="font-semibold text-rose-300">1</span> = Brouillon / Formel / En préparation</li>
                          <li><span className="font-semibold text-rose-300">2</span> = Documenté / Opérationnel</li>
                          <li><span className="font-semibold text-rose-300">3</span> = Conforme (objectif échéances 2025)</li>
                          <li><span className="font-semibold text-rose-300">4</span> = Intégré / Efficace</li>
                          <li><span className="font-semibold text-rose-300">5</span> = Exemplaire</li>
                        </ul>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'raci' && (
                    <motion.div
                      key="raci"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="space-y-6"
                    >
                      <SectionTitle
                        title="Rôles & responsabilités (RACI)"
                        subtitle="Owner, Responsable et Consulté pour le cadre Loi 27 ; plus les rôles requis par la loi (RSS, comité paritaire)."
                      />
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-rose-500/20 rounded-xl overflow-hidden">
                          <thead>
                            <tr className="bg-rose-500/20 text-left">
                              <th className="p-3 text-white font-bold">Rôle / Activité</th>
                              <th className="p-3 text-white font-bold">
                                <div className="flex items-center gap-2">
                                  <img src={AVATAR_YM} alt="Dany" className="w-7 h-7 rounded-full border-2 border-rose-400" />
                                  <span>Dany (Owner)</span>
                                </div>
                              </th>
                              <th className="p-3 text-white font-bold">
                                <div className="flex items-center gap-2">
                                  <img src={AVATAR_MP} alt="Mélissa" className="w-7 h-7 rounded-full border-2 border-rose-400" />
                                  <span>Mélissa (R)</span>
                                </div>
                              </th>
                              <th className="p-3 text-white font-bold">
                                <div className="flex items-center gap-2">
                                  <img src={AVATAR_DB} alt="Dany" className="w-7 h-7 rounded-full border-2 border-rose-400" />
                                  <span>Dany (C)</span>
                                </div>
                              </th>
                              <th className="p-3 text-white font-bold">Autres (Loi 27)</th>
                            </tr>
                          </thead>
                          <tbody className="text-gray-200">
                            <tr className="border-t border-rose-500/20 bg-white/5">
                              <td className="p-3">Pilotage global Loi 27 / comité RH</td>
                              <td className="p-3 font-semibold text-rose-200">A (Owner)</td>
                              <td className="p-3">R</td>
                              <td className="p-3">C</td>
                              <td className="p-3">—</td>
                            </tr>
                            <tr className="border-t border-rose-500/20 bg-white/5">
                              <td className="p-3">Programme de prévention, PV comité SST, registres</td>
                              <td className="p-3">I</td>
                              <td className="p-3 font-semibold text-rose-200">R</td>
                              <td className="p-3">C</td>
                              <td className="p-3">Comité paritaire, RSS</td>
                            </tr>
                            <tr className="border-t border-rose-500/20 bg-white/5">
                              <td className="p-3">RPS : politique, identification, suivi</td>
                              <td className="p-3">I</td>
                              <td className="p-3 font-semibold text-rose-200">R</td>
                              <td className="p-3">C</td>
                              <td className="p-3">Comité SST</td>
                            </tr>
                            <tr className="border-t border-rose-500/20 bg-white/5">
                              <td className="p-3">Décisions stratégiques (budget, orientations)</td>
                              <td className="p-3 font-semibold text-rose-200">A</td>
                              <td className="p-3">R</td>
                              <td className="p-3">C</td>
                              <td className="p-3">—</td>
                            </tr>
                            <tr className="border-t border-rose-500/20 bg-white/5">
                              <td className="p-3">Formation SST / RPS (gestionnaires et employés)</td>
                              <td className="p-3">I</td>
                              <td className="p-3 font-semibold text-rose-200">R</td>
                              <td className="p-3">C</td>
                              <td className="p-3">RSS peut coanimer</td>
                            </tr>
                            <tr className="border-t border-rose-500/20 bg-white/5">
                              <td className="p-3">Préparation / accueil inspection CNESST</td>
                              <td className="p-3">I</td>
                              <td className="p-3 font-semibold text-rose-200">R</td>
                              <td className="p-3">C</td>
                              <td className="p-3">Comité SST, RSS</td>
                            </tr>
                          </tbody>
                        </table>
                        <p className="text-xs text-gray-400 mt-2">A = Responsable (Accountable), R = Réalise (Responsible), I = Informé, C = Consulté.</p>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                          <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-rose-300" />
                            Rôles permanents (gouvernance Loi 27)
                          </h4>
                          <div className="space-y-4">
                            <div className="flex items-start gap-3">
                              <img src={AVATAR_YM} alt="Dany Bourassa" className="w-10 h-10 rounded-full border-2 border-rose-400 shrink-0" />
                              <div>
                                <p className="font-semibold text-white">Dany Bourassa — Owner</p>
                                <p className="text-sm text-gray-300">Pilote le cadre, valide les orientations, responsable final.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <img src={AVATAR_MP} alt="Mélissa Pelletier" className="w-10 h-10 rounded-full border-2 border-rose-400 shrink-0" />
                              <div>
                                <p className="font-semibold text-white">Mélissa Pelletier — Responsable (R)</p>
                                <p className="text-sm text-gray-300">Opération : programme, comité SST, RSS, RPS, PV, registres.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <img src={AVATAR_DB} alt="Dany Bourassa" className="w-10 h-10 rounded-full border-2 border-rose-400 shrink-0" />
                              <div>
                                <p className="font-semibold text-white">Dany Bourassa — Consulté (C)</p>
                                <p className="text-sm text-gray-300">Conseil, alignement stratégique, communication.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <img src={AVATAR_GC} alt="Guillaume Cloutier" className="w-10 h-10 rounded-full border-2 border-rose-400 shrink-0" />
                              <div>
                                <p className="font-semibold text-white">Guillaume Cloutier — Membre</p>
                                <p className="text-sm text-gray-300">Membre du comité RH & Loi 27.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <InfoCard
                          title="Rôles requis par la Loi 27"
                          icon={<FileText className="w-5 h-5" />}
                          bullets={[
                            "Représentant SST (RSS) : désigné, formé, connu — interface employeur/travailleurs.",
                            "Comité SST paritaire : membres employeur + travailleurs, réunions, recommandations et suivi.",
                          ]}
                        />
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'roadmap' && (
                    <motion.div
                      key="roadmap"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="space-y-6"
                    >
                      <SectionTitle
                        title="Roadmap / Échéancier"
                        subtitle="Jalons légaux et plan d'action concret pour gestionnaires et PME."
                      />

                      {/* Jalons légaux */}
                      <div>
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-rose-300" />
                          Jalons conformité Loi 27
                        </h4>
                        <div className="space-y-4">
                          <div className="flex gap-4 items-start">
                            <div className="w-3 h-3 rounded-full bg-rose-500 mt-1.5 shrink-0" />
                            <div>
                              <p className="font-semibold text-white">Dès maintenant</p>
                              <p className="text-sm text-gray-300">Gouvernance documentée (cette carte), RACI en place, désignation RSS et composition comité SST à finaliser.</p>
                            </div>
                          </div>
                          <div className="flex gap-4 items-start">
                            <div className="w-3 h-3 rounded-full bg-rose-500 mt-1.5 shrink-0" />
                            <div>
                              <p className="font-semibold text-white">1er octobre 2025</p>
                              <p className="text-sm text-gray-300">Programme de prévention à jour, comité SST opérationnel, RSS en poste, réunions et PV conformes.</p>
                            </div>
                          </div>
                          <div className="flex gap-4 items-start">
                            <div className="w-3 h-3 rounded-full bg-pink-500 mt-1.5 shrink-0" />
                            <div>
                              <p className="font-semibold text-white">6 octobre 2025</p>
                              <p className="text-sm text-gray-300">Risques psychosociaux intégrés (identification, politique, formation, suivi).</p>
                            </div>
                          </div>
                          <div className="flex gap-4 items-start">
                            <div className="w-3 h-3 rounded-full bg-rose-400/70 mt-1.5 shrink-0" />
                            <div>
                              <p className="font-semibold text-white">Continu</p>
                              <p className="text-sm text-gray-300">Révision périodique du programme, du comité et des RPS ; amélioration continue.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Plan d'action concret (Mélissa) */}
                      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <Target className="w-5 h-5 text-pink-300" />
                          Plan d'action concret pour gestionnaires et PME
                        </h4>
                        <p className="text-sm text-gray-300 mb-6">
                          Plan d'action séquencé à adapter selon la taille de l'entreprise. Préparé par Mélissa pour structurer la mise en œuvre Loi 27 et RPS.
                        </p>
                        <div className="space-y-5">
                          {/* 1. Sensibilisation */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">1. Sensibilisation</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Organiser une formation de base (gestionnaires + employés) sur la Loi 27, les RPS et les obligations légales.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Délai :</span> Le plus rapidement possible · <span className="text-pink-200">Responsables :</span> RH, santé et sécurité, externe si besoin.</p>
                          </div>
                          {/* 2. Diagnostic terrain */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">2. Diagnostic terrain</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Recueillir des indicateurs existants (absentéisme, turnover, plaintes, rencontres 1:1) + sondage structuré qualitatif sur les risques psychosociaux.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Délai :</span> D'ici 1 à 2 mois · <span className="text-pink-200">Responsables :</span> Gestionnaires, RH, consultant externe si besoin.</p>
                          </div>
                          {/* 3. Analyse des résultats */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">3. Analyse des résultats</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Identifier les principaux facteurs de RPS propres à votre contexte.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Délai :</span> 1 mois après le diagnostic · <span className="text-pink-200">Responsables :</span> Comité SST, agent de liaison, gestionnaires.</p>
                          </div>
                          {/* 4. Élaboration du plan d'action */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">4. Élaboration du plan d'action</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Définir des mesures ciblées (ex. réviser la charge de travail, renforcer soutien, clarifier rôles).</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Délai :</span> Immédiatement après l'étape 3 · <span className="text-pink-200">Responsables :</span> Comité SST, gestionnaires.</p>
                          </div>
                          {/* 5. Mise en œuvre pilote */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">5. Mise en œuvre pilote du plan</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Tester quelques mesures dans une équipe / département avant un déploiement plus large.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Délai :</span> 3 à 6 mois après l'étape 4 · <span className="text-pink-200">Responsables :</span> Gestionnaires, équipes pilotes.</p>
                          </div>
                          {/* 6. Suivi et ajustement */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">6. Suivi et ajustement</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Mesurer l'impact (indicateurs, feedback) et ajuster les mesures.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Fréquence :</span> Périodiquement (ex. trimestriel) · <span className="text-pink-200">Responsables :</span> Comité SST, gestionnaires, RH.</p>
                          </div>
                          {/* 7. Documentation */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">7. Documentation</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Conserver tous les rapports, procès-verbaux, preuves de communication et ajustements.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Fréquence :</span> En continu · <span className="text-pink-200">Responsables :</span> RH, Comité SST, gestionnaires.</p>
                          </div>
                          {/* 8. Communication et transparence */}
                          <div className="border-l-2 border-rose-500/50 pl-4 py-2">
                            <p className="font-semibold text-white">8. Communication et transparence</p>
                            <p className="text-sm text-gray-300 mt-1"><span className="text-rose-200">Action :</span> Partager les progrès, les mesures prises et les résultats avec les employés.</p>
                            <p className="text-xs text-gray-400 mt-1"><span className="text-pink-200">Fréquence :</span> Régulièrement · <span className="text-pink-200">Responsables :</span> Direction + gestionnaires.</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'rituels' && (
                    <motion.div
                      key="rituels"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="space-y-6"
                    >
                      <SectionTitle
                        title="Gouvernance & rituels"
                        subtitle="Fréquence, ordre du jour type, PV, registres et checklist de documentation pour la conformité."
                      />

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                          <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-rose-300" />
                            Comité SST (paritaire)
                          </h4>
                          <ul className="space-y-2 text-sm text-gray-200 list-disc ml-5">
                            <li>Réunions à la fréquence prévue par la loi et le règlement (pour 20+ travailleurs, souvent mensuelles ou selon entente).</li>
                            <li>Convocation envoyée à l’avance avec ordre du jour.</li>
                            <li>Procès-verbaux rédigés, datés, conservés ; copies aux membres et à l’employeur.</li>
                            <li>Recommandations tracées avec statut (accepté / refusé / en cours) et propriétaire.</li>
                          </ul>
                        </div>
                        <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                          <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-rose-300" />
                            Qui fait quoi (PV et registres)
                          </h4>
                          <ul className="space-y-2 text-sm text-gray-200 list-disc ml-5">
                            <li>Secrétaire du comité (ou RH) : rédaction des PV, envoi des convocations, mise à jour du registre des recommandations.</li>
                            <li>Président (ou représentant employeur) : animation, validation des PV.</li>
                            <li>RSS : participation, point de vue travailleurs, suivi des dossiers terrain.</li>
                            <li>RH (Mélissa) : coordination avec le programme de prévention, RPS, formations et preuves.</li>
                          </ul>
                        </div>
                      </div>

                      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-rose-300" />
                          Ordre du jour type — Réunion du comité SST
                        </h4>
                        <ol className="space-y-1 text-sm text-gray-200 list-decimal ml-5">
                          <li>Ouverture et adoption de l’ordre du jour</li>
                          <li>Lecture et adoption du procès-verbal de la réunion précédente</li>
                          <li>Accidents, incidents et premiers secours (depuis dernière réunion)</li>
                          <li>Suivi des recommandations en cours (statut, responsable, échéance)</li>
                          <li>Risques psychosociaux (RPS) — indicateurs, plaintes, mesures, formation</li>
                          <li>Programme de prévention — révision, communication, points à mettre à jour</li>
                          <li>Formation et sensibilisation SST / RPS</li>
                          <li>Divers et prochaine réunion</li>
                        </ol>
                      </div>

                      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                        <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                          <ShieldCheck className="w-5 h-5 text-rose-300" />
                          Checklist — Documentation à conserver (preuves conformité)
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-200">
                          <li>· Programme de prévention (version à jour + preuve de communication aux travailleurs)</li>
                          <li>· Convocation et procès-verbaux du comité SST (chronologique)</li>
                          <li>· Registre des recommandations du comité (avec statut et propriétaire)</li>
                          <li>· Désignation du RSS (PV ou écrit) et attestation de formation</li>
                          <li>· Politique RPS et preuves de formation (gestionnaires et employés)</li>
                          <li>· Registre des accidents/incidents et actions correctives</li>
                          <li>· Dates de révision du programme et des politiques</li>
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Modal Guide Réunion — Exemple ordre du jour */}
      <AnimatePresence>
        {isGuideReunionOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsGuideReunionOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-slate-900 via-rose-900/50 to-slate-900 rounded-2xl w-[90vw] max-w-4xl max-h-[90vh] border border-rose-500/30 shadow-2xl shadow-rose-500/10 flex flex-col">
                {/* Header */}
                <div className="relative p-4 border-b border-rose-500/30 bg-gradient-to-r from-rose-900/40 to-slate-900/40 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-pink-300">
                          Guide réunion — Exemple ordre du jour Comité SST
                        </h2>
                        <p className="text-gray-300 text-xs">Modèle complet pour tenir une réunion conforme et efficace</p>
                      </div>
                    </div>
                    <button onClick={() => setIsGuideReunionOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
                      <X className="w-5 h-5 text-gray-300" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                  <div className="space-y-6">
                    {/* En-tête exemple */}
                    <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
                      <div className="space-y-2 text-sm text-gray-200">
                        <p><strong className="text-white">Entreprise :</strong> BZ inc.</p>
                        <p><strong className="text-white">Comité :</strong> Comité SST paritaire</p>
                        <p><strong className="text-white">Date :</strong> [Date de la réunion]</p>
                        <p><strong className="text-white">Heure :</strong> [Heure]</p>
                        <p><strong className="text-white">Lieu :</strong> [Lieu ou visioconférence]</p>
                        <p><strong className="text-white">Président :</strong> [Nom du président]</p>
                        <p><strong className="text-white">Secrétaire :</strong> [Nom du secrétaire — souvent Mélissa]</p>
                      </div>
                    </div>

                    {/* Ordre du jour */}
                    <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
                      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-rose-300" />
                        Ordre du jour
                      </h3>
                      <ol className="space-y-4 text-sm text-gray-200">
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">1.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Ouverture et adoption de l'ordre du jour</p>
                            <p className="text-gray-300 text-xs">Vérifier la présence (quorum), adopter l'ordre du jour, désigner le secrétaire si nécessaire.</p>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">2.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Lecture et adoption du procès-verbal de la réunion précédente</p>
                            <p className="text-gray-300 text-xs">Lire le PV, corriger si nécessaire, adopter. <strong className="text-rose-200">Important :</strong> garder une trace des corrections.</p>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">3.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Accidents, incidents et premiers secours (depuis dernière réunion)</p>
                            <p className="text-gray-300 text-xs mb-2">Faire le point sur :</p>
                            <ul className="list-disc ml-5 space-y-1 text-xs text-gray-400">
                              <li>Nombre d'accidents/incidents déclarés</li>
                              <li>Nature des incidents (ex. : chute, coupure, malaise)</li>
                              <li>Actions prises (premiers secours, déclaration CNESST si applicable)</li>
                              <li>Mesures préventives mises en place</li>
                            </ul>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">4.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Suivi des recommandations en cours</p>
                            <p className="text-gray-300 text-xs mb-2">Pour chaque recommandation en cours :</p>
                            <ul className="list-disc ml-5 space-y-1 text-xs text-gray-400">
                              <li>Description de la recommandation</li>
                              <li>Statut (accepté / refusé / en cours / terminé)</li>
                              <li>Responsable et échéance</li>
                              <li>Progrès réalisés depuis la dernière réunion</li>
                              <li>Blocages ou besoins identifiés</li>
                            </ul>
                            <p className="text-gray-300 text-xs mt-2"><strong className="text-rose-200">Astuce :</strong> Utiliser un tableau de suivi (recommandation | statut | responsable | échéance | progrès).</p>
                            <p className="text-gray-300 text-xs mt-2"><strong className="text-rose-200">Chez BZ :</strong> Le suivi des recommandations sera géré par le plan tactique dans TactiCE.</p>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">5.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Risques psychosociaux (RPS)</p>
                            <p className="text-gray-300 text-xs mb-2"><strong className="text-pink-200">Obligatoire depuis le 6 octobre 2025</strong> — Faire le point sur :</p>
                            <ul className="list-disc ml-5 space-y-1 text-xs text-gray-400">
                              <li>Indicateurs RPS (sondages, plaintes, absences liées au stress, turnover)</li>
                              <li>Plaintes ou situations signalées (harcèlement, violence, charge de travail excessive)</li>
                              <li>Mesures prises ou prévues (formation, ajustement charge, soutien)</li>
                              <li>Formation RPS dispensée (gestionnaires et employés)</li>
                              <li>Révision de la politique RPS si nécessaire</li>
                            </ul>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">6.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Programme de prévention</p>
                            <p className="text-gray-300 text-xs mb-2">Vérifier :</p>
                            <ul className="list-disc ml-5 space-y-1 text-xs text-gray-400">
                              <li>Le programme est à jour et accessible aux travailleurs</li>
                              <li>Nouveaux risques identifiés depuis la dernière réunion</li>
                              <li>Mesures de prévention mises en place</li>
                              <li>Date de la prochaine révision (au moins annuelle)</li>
                              <li>Communication du programme aux nouveaux employés</li>
                            </ul>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">7.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Formation et sensibilisation SST / RPS</p>
                            <p className="text-gray-300 text-xs mb-2">Faire le point sur :</p>
                            <ul className="list-disc ml-5 space-y-1 text-xs text-gray-400">
                              <li>Formations dispensées depuis la dernière réunion</li>
                              <li>Formations prévues (calendrier)</li>
                              <li>Besoins de formation identifiés</li>
                              <li>Participation des employés (taux de présence)</li>
                            </ul>
                          </div>
                        </li>
                        <li className="flex gap-3">
                          <span className="font-bold text-rose-300 shrink-0">8.</span>
                          <div>
                            <p className="font-semibold text-white mb-1">Divers et prochaine réunion</p>
                            <p className="text-gray-300 text-xs mb-2">Points divers, questions des membres, date et heure de la prochaine réunion.</p>
                          </div>
                        </li>
                      </ol>
                    </div>

                    {/* Conseils pratiques */}
                    <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/30">
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                        Conseils pratiques pour être sur la coche
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li>· <strong className="text-white">Envoyer la convocation</strong> avec l'ordre du jour au moins 48h à l'avance</li>
                        <li>· <strong className="text-white">Préparer les documents</strong> (PV précédent, tableau de suivi des recommandations, indicateurs)</li>
                        <li>· <strong className="text-white">Respecter la durée</strong> (1h à 1h30 max) et garder le focus sur les points à l'ordre du jour</li>
                        <li>· <strong className="text-white">Rédiger le PV</strong> dans les 48h après la réunion, le faire valider par le président, puis l'envoyer à tous les membres</li>
                        <li>· <strong className="text-white">Suivre les recommandations</strong> : mettre à jour le tableau de suivi avant chaque réunion</li>
                        <li>· <strong className="text-white">Inclure les RPS</strong> systématiquement (obligatoire depuis le 6 octobre 2025)</li>
                        <li>· <strong className="text-white">Conserver les PV</strong> : garder une copie chronologique accessible (dossier partagé ou intranet)</li>
                      </ul>
                    </div>

                    {/* Template PV */}
                    <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-rose-300" />
                        Structure type du procès-verbal
                      </h4>
                      <div className="space-y-2 text-sm text-gray-200">
                        <p>1. En-tête (entreprise, comité, date, heure, lieu, présents, absents)</p>
                        <p>2. Adoption de l'ordre du jour</p>
                        <p>3. Adoption du PV précédent</p>
                        <p>4. Compte-rendu de chaque point à l'ordre du jour (décisions, recommandations, actions)</p>
                        <p>5. Liste des recommandations (description, statut, responsable, échéance)</p>
                        <p>6. Date de la prochaine réunion</p>
                        <p>7. Signature du président et du secrétaire</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}
